import { ContextMenuWrapper } from "components/ContextMenu/ContextMenu";
import RecordLabel from "components/RecordLabel";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { resources } from "config";
import { ExecutionStatus } from "config/ts-utils";
import dayjs from "dayjs";
import { getRelativeUrl } from "utils/service";
import { v4 as uuidv4 } from "uuid";

export const genericOverTimeChartDto = (dataAPI = {}) => {
    const { execution_status_per_time: statData } = dataAPI;
    const successExecutions = statData?.filter(
        (stat) => stat?.status === ExecutionStatus.ENDED_SUCCESS
    );
    const errorExecutions = statData?.filter(
        (stat) => stat?.status === ExecutionStatus.ENDED_ERROR
    );
    const cancelledExecutions = statData?.filter(
        (stat) => stat?.status === ExecutionStatus.ENDED_CANCELLED
    );
    const daysSuccessfulExecutions = [];
    const daysErrorExecutions = [];

    for (let i = 6; i >= 0; i -= 1) {
        const day = dayjs().subtract(i, "days");
        const dayFilterFormat = day.format("YYYY-MM-DD");
        const daySuccessExecutions =
            successExecutions?.filter((execution) =>
                execution?.time?.includes(dayFilterFormat)
            )[0] || 0;
        const dayErrorExecutions =
            errorExecutions?.filter((execution) => execution?.time?.includes(dayFilterFormat))[0] ||
            0;
        const dayCancelledExecutions =
            cancelledExecutions?.filter((execution) =>
                execution?.time?.includes(dayFilterFormat)
            )[0] || 0;
        daysSuccessfulExecutions.push(daySuccessExecutions?.count || 0);
        daysErrorExecutions.push(
            (dayErrorExecutions?.count || 0) + (dayCancelledExecutions?.count || 0)
        );
    }

    return [
        {
            label: "Successful",
            backgroundColor: "#2e8c00",
            barThickness: 10,
            data: daysSuccessfulExecutions,
        },
        {
            label: "Failed",
            backgroundColor: "#bf0026",
            barThickness: 10,
            data: daysErrorExecutions,
        },
    ];
};

const recordLabel = ({ is_deleted, identity_type, id, name, icon }) => {
    const childrenProps = {
        link: is_deleted ? null : getRelativeUrl(identity_type, id),
        name: is_deleted ? `${name} (deleted)` : name,
        icon,
    };
    return (
        <div className="identity-link">
            <RecordLabel {...childrenProps} />
        </div>
    );
};

export const createdByAtDto = (data) => {
    const { created_at, created_by_identity } = data;

    let created_by;
    if (created_by_identity) {
        const { identity_type, id, name, is_deleted } = created_by_identity;
        const icon = resources.find(
            (resource) => resource?.resourceName === identity_type.toLowerCase()
        )?.icon;
        created_by = recordLabel({ is_deleted, identity_type, id, name, icon });
    } else {
        created_by = "Unknown";
    }
    return (
        <>
            {created_by}
            <TimeAgo datetime={created_at} />
        </>
    );
};

export const createdAtDto = (data, setNewValue) => {
    const { created_at } = data;

    return setNewValue ? (
        <ContextMenuWrapper
            handleClick={() =>
                setNewValue({
                    field: "created_at",
                    value: created_at,
                })
            }
        >
            <TimeAgo datetime={created_at} />
        </ContextMenuWrapper>
    ) : (
        <TimeAgo datetime={created_at} />
    );
};

export const createdByDto = (data, setNewValue) => {
    const { created_by_identity } = data;

    if (created_by_identity) {
        const { identity_type, id, name, is_deleted } = created_by_identity;
        const icon = resources.find(
            (resource) => resource?.resourceName === identity_type.toLowerCase()
        )?.icon;

        const children = recordLabel({ is_deleted, identity_type, id, name, icon });

        if (setNewValue) {
            return (
                <ContextMenuWrapper
                    handleClick={() =>
                        setNewValue({
                            field: "created_by--identity.id:name",
                            value: name,
                        })
                    }
                >
                    {children}
                </ContextMenuWrapper>
            );
        } else {
            return children;
        }
    }
    return null;
};

export const modifiedByAtDto = (data) => {
    const { modified_at, modified_by_identity } = data;
    if (!modified_at || !modified_by_identity) return "";
    let modified_by;
    const { identity_type, id, name, is_deleted } = modified_by_identity;
    const icon = resources.find(
        (resource) => resource?.resourceName === identity_type.toLowerCase()
    )?.icon;
    modified_by = recordLabel({ is_deleted, identity_type, id, name, icon });

    return (
        <>
            {modified_by}
            <TimeAgo datetime={modified_at} />
        </>
    );
};

export const modifiedAtDto = (data, setNewValue) => {
    const { modified_at } = data;
    if (!modified_at) return null;

    if (setNewValue) {
        return (
            <ContextMenuWrapper
                handleClick={() =>
                    setNewValue({
                        field: "modified_at",
                        value: modified_at,
                    })
                }
            >
                <TimeAgo datetime={modified_at} />
            </ContextMenuWrapper>
        );
    } else {
        return <TimeAgo datetime={modified_at} />;
    }
};

export const modifiedByDto = (data, setNewValue) => {
    const { modified_by_identity } = data;
    if (!modified_by_identity) return null;

    const { identity_type, id, name, is_deleted } = modified_by_identity;
    const icon = resources.find(
        (resource) => resource?.resourceName === identity_type.toLowerCase()
    )?.icon;

    const children = recordLabel({ is_deleted, identity_type, id, name, icon });

    if (setNewValue) {
        return (
            <ContextMenuWrapper
                handleClick={() =>
                    setNewValue({
                        field: "modified_by--identity.id:name",
                        value: name,
                    })
                }
            >
                {children}
            </ContextMenuWrapper>
        );
    } else {
        return children;
    }
};

export const defaultResourcesDetailsSidebarItems = [
    {
        label: "ID",
        valueKey: "id",
        dto: (value) => value,
    },
    {
        label: "Created by",
        dto: createdByDto,
    },
    {
        label: "Created at",
        valueKey: "created_at",
        dto: (value) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
    },
    {
        label: "Last modified by",
        dto: modifiedByDto,
    },
    {
        label: "Modified at",
        valueKey: "modified_at",
        dto: (value) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
    },
    {
        label: "Trashed at",
        valueKey: "deleted_at",
        dto: (value) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
    },
];

export const getRandomOtpSecret = () => {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567"; // copied from pyotp.random_base32()
    const charactersLength = characters.length;
    // Use a cryptographically secure RNG — Math.random() is predictable and must not be
    // used to generate a 2FA/TOTP secret. Reject-sample to avoid modulo bias across the
    // 32-char (5-bit) alphabet.
    const randomValues = new Uint8Array(32);
    crypto.getRandomValues(randomValues);
    for (let i = 0; i < 32; i += 1) {
        let value = randomValues[i];
        while (value >= 256 - (256 % charactersLength)) {
            const replacement = new Uint8Array(1);
            crypto.getRandomValues(replacement);
            value = replacement[0];
        }
        result += characters.charAt(value % charactersLength);
    }
    return result;
};

export const get2FAProvisioningUri = (otpSecret, email) => {
    // TODO: add workspace name to issuer string
    const issuer = "Cloudomation";
    return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(
        email
    )}:${encodeURIComponent(uuidv4())}?secret=${encodeURIComponent(
        otpSecret
    )}&issuer=${encodeURIComponent(issuer)}`;
};
